import React from 'react';
import { Button, IconButton } from '@fiverr-private/button';
import { ArrowLeftIcon } from '@fiverr-private/visuals';
import { Container } from '@fiverr-private/layout_components';
import { translate } from '../../../utils/translate';
import { Variant } from '../../../types';
import { imageMP, imageMPLowQ, imagePro, imageProLowQ, imageVariantGo, imageVariantGoLowQ } from '../../../constants';
import { useBreakpointMd } from '../../../utils/breakpoints';
import { useCodeActivationContext } from '../../shared/CodeActivationContext/CodeActivationContext';
import { FiverrLogo } from '../../shared/FiverrLogo';
import { FiverrProLogo } from '../../shared/FiverrProLogo';
import { BlurredImage } from '../../shared/BlurredImage';
import { CodeActivationStep } from '../../CodeActivationStep';
import { ChangeEmailStep } from '../../ChangeEmailStep';
import * as classes from './style.ve.css';

const getIllustrations = (isPro: boolean, variant: Variant) => {
    if (isPro) {
        return {
            image: imagePro,
            imageLowQ: imageProLowQ,
        };
    }
    if (variant === 'go') {
        return {
            image: imageVariantGo,
            imageLowQ: imageVariantGoLowQ,
        };
    }
    return {
        image: imageMP,
        imageLowQ: imageMPLowQ,
    };
};

export const ActiveStep = () => {
    const isMdScreen = useBreakpointMd();
    const {
        stepId,
        isPro,
        email,
        postActivationDelay,
        variant,
        onSkip,
        onSuccess,
        onError,
        goToStep,
        setEmail,
        initialFocusRef,
    } = useCodeActivationContext();
    const hasBack = stepId === 'change_email';

    const renderStep = () => {
        switch (stepId) {
            case 'code_activation':
                return (
                    <CodeActivationStep
                        email={email}
                        variant={variant}
                        postActivationDelay={postActivationDelay}
                        onSkip={onSkip}
                        onError={onError}
                        onSuccess={onSuccess}
                        onGoToChangeEmail={() => goToStep('change_email')}
                        initialFocusRef={initialFocusRef}
                    />
                );
            case 'change_email':
                return (
                    <ChangeEmailStep
                        email={email}
                        onEmailChanged={(email: string) => {
                            setEmail(email);
                            goToStep('code_activation');
                        }}
                    />
                );
            default: {
                const _check: never = stepId; // eslint-disable-line @typescript-eslint/no-unused-vars
                return null;
            }
        }
    };

    const { image, imageLowQ } = getIllustrations(isPro, variant);

    return (
        <Container
            gap="0"
            display="flex"
            height={{ default: '100%', sm: '645px' }}
            direction={isPro ? 'rowReverse' : 'row'}
            overflowY="auto"
        >
            {isMdScreen && (
                <BlurredImage flex="1" src={image} fallbackSrc={imageLowQ} alt={translate('images.banner')} />
            )}
            <Container flex="1" display="flex" direction="column">
                <Container
                    paddingX={{ default: '6', sm: '10' }}
                    height={{ default: 60, sm: 72 }}
                    display="flex"
                    justifyContent="spaceBetween"
                    alignItems="center"
                >
                    {hasBack && (
                        <>
                            {isMdScreen ? (
                                <Button
                                    variant="ghost"
                                    onClick={() => goToStep('code_activation')}
                                    size="sm"
                                    className={classes.backButton}
                                >
                                    <ArrowLeftIcon />
                                    {translate('general.back')}
                                </Button>
                            ) : (
                                <IconButton
                                    aria-label="back"
                                    variant="ghost"
                                    onClick={() => goToStep('code_activation')}
                                    className={classes.backButton}
                                >
                                    <ArrowLeftIcon color="currentColor" />
                                </IconButton>
                            )}
                        </>
                    )}
                    <Container
                        display={{ default: 'flex', md: 'none' }}
                        direction="column"
                        flex="1"
                        paddingRight="4"
                        alignItems={hasBack ? 'center' : 'flexStart'}
                    >
                        {isPro ? <FiverrProLogo /> : <FiverrLogo />}
                    </Container>
                </Container>
                <Container
                    flex="1"
                    paddingX={{ default: '6', sm: '10' }}
                    paddingTop={{ default: '8', md: '0' }}
                    paddingBottom={{ default: '4', sm: '8' }}
                >
                    {renderStep()}
                </Container>
            </Container>
        </Container>
    );
};
